import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import PropTypes from 'prop-types';

import { useIntl } from '../../../util/reactIntl';
import { IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';
import css from './ImageCarousel.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showFullscreenButton: false,
  slideDuration: 350,
};

const ImageCarousel = props => {
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, startIndex = 0 } = props;
  const [currentIndex, setIndex] = useState(startIndex);

  // ✅ Construct gallery items with original + thumbnail URLs
  const items = images.map((img, i) => {
    const variants = img?.attributes?.variants || {};

    const original =
      variants['scaled-large']?.url ||
      variants['scaled-xlarge']?.url ||
      variants['default']?.url ||
      '';

    const thumbnail =
      variants['scaled-small']?.url ||
      variants['scaled-medium']?.url ||
      original;

    return {
      original,
      thumbnail,
      alt: intl.formatMessage(
        { id: 'ImageCarousel.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      image: img,
    };
  });

  // ✅ Custom item renderer (main image)
  const renderItem = item => (
    <div className={css.imageWrapper}>
      <div className={css.itemCentering}>
        <ResponsiveImage
          rootClassName={css.item}
          image={item.image}
          alt={item.alt}
          variants={imageVariants}
          sizes="(max-width: 767px) 100vw, 80vw"
        />
      </div>
    </div>
  );

  // ✅ Custom navigation
  const renderLeftNav = (onClick, disabled) => (
    <button className={css.navLeft} disabled={disabled} onClick={onClick}>
      <div className={css.navArrowWrapper}>
        <IconArrowHead direction="left" size="big" className={css.arrowHead} />
      </div>
    </button>
  );

  const renderRightNav = (onClick, disabled) => (
    <button className={css.navRight} disabled={disabled} onClick={onClick}>
      <div className={css.navArrowWrapper}>
        <IconArrowHead direction="right" size="big" className={css.arrowHead} />
      </div>
    </button>
  );

  // ✅ Handle empty state
  if (items.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={classes} image={null} variants={[]} alt="" />;
  }

  const handleSlide = current => setIndex(current);
  const naturalIndex = index => index + 1;

  const imageIndex =
    items.length > 0 ? (
      <span className={css.imageIndex}>
        {naturalIndex(currentIndex)}/{items.length}
      </span>
    ) : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onSlide={handleSlide}
        startIndex={startIndex}
        showThumbnails={true}
        thumbnailPosition="left"
        {...IMAGE_GALLERY_OPTIONS}
      />
      {/* {imageIndex} */}
    </>
  );
};

// ✅ Correct PropTypes definition
ImageCarousel.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  images: PropTypes.array.isRequired,
  imageVariants: PropTypes.array,
  startIndex: PropTypes.number,
};

export default ImageCarousel;
